import {
  db,
  type LocalEvent,
  type LocalJob,
  type LocalPhoto,
  type LocalWorkingDay,
} from "./db";
import { pushSync, pullSync, uploadPhoto, retrieveWorkingDay } from "./api";
import type { SyncStatus } from "../../shared/types";

export type StatusListener = (status: SyncStatus, detail?: string) => void;

let listener: StatusListener | null = null;
let syncing = false;

export function onStatus(fn: StatusListener) {
  listener = fn;
}

function setStatus(status: SyncStatus, detail?: string) {
  listener?.(status, detail);
}

export async function runSync(): Promise<void> {
  if (syncing) return;
  if (!navigator.onLine) {
    setStatus("Saved on this device", "Offline");
    return;
  }

  syncing = true;
  setStatus("Syncing");

  try {
    const [workingDays, jobs, events, photos] = await Promise.all([
      db.workingDays.toArray(),
      db.jobs.toArray(),
      db.events.toArray(),
      db.photos.toArray(),
    ]);

    const pendingDays = workingDays.filter((d) => d.syncStatus !== "Synced");
    const pendingJobs = jobs.filter((j) => j.syncStatus !== "Synced");
    const pendingEvents = events.filter((e) => e.syncStatus !== "Synced");
    const pendingPhotos = photos.filter((p) => p.syncStatus !== "Synced");

    const result = await pushSync({
      workingDays: pendingDays.map(stripLocal),
      jobs: pendingJobs.map(stripLocal),
      events: pendingEvents.map(stripLocal),
      photos: pendingPhotos.map(stripPhotoMeta),
    });

    // Upload photo blobs after metadata sync.
    for (const photo of pendingPhotos) {
      if (!photo.blob) continue;
      if (photo.verificationStatus === "verified") continue;
      try {
        const upload = await uploadPhoto(photo.id, photo.blob, photo.sha256);
        await db.photos.update(photo.id, {
          verificationStatus: upload.serverConfirmed
            ? "verified"
            : (upload.verificationStatus as LocalPhoto["verificationStatus"]),
          syncStatus: upload.serverConfirmed ? "Synced" : "Pending sync",
        });
      } catch {
        await db.photos.update(photo.id, { syncStatus: "Failed" });
      }
    }

    // Merge server copy without flicker: update IndexedDB in place.
    await mergeServer(result);

    if (result.conflicts.length) {
      setStatus("Conflict", result.conflicts.join(", "));
    } else {
      setStatus("Synced");
    }
  } catch (err) {
    console.error(err);
    setStatus("Failed", err instanceof Error ? err.message : "Sync failed");
  } finally {
    syncing = false;
  }
}

export async function retrieveAndMerge(workingDayId: string): Promise<unknown> {
  const remote = await retrieveWorkingDay(workingDayId);
  const pull = await pullSync();
  await mergeServer(pull);
  setStatus("Synced");
  return remote;
}

async function mergeServer(result: Awaited<ReturnType<typeof pushSync>>) {
  await db.transaction(
    "rw",
    db.workingDays,
    db.jobs,
    db.events,
    db.photos,
    async () => {
      for (const day of result.workingDays) {
        const local = await db.workingDays.get(day.id);
        const keepLocal =
          local &&
          local.syncStatus === "Pending sync" &&
          local.clientUpdatedAt > day.clientUpdatedAt;
        if (keepLocal) continue;
        await db.workingDays.put({ ...day, syncStatus: "Synced" });
      }
      for (const job of result.jobs) {
        const local = await db.jobs.get(job.id);
        if (local && local.syncStatus === "Pending sync" && local.clientUpdatedAt > job.clientUpdatedAt) {
          continue;
        }
        await db.jobs.put({ ...job, syncStatus: "Synced" });
      }
      for (const event of result.events) {
        const local = await db.events.get(event.id);
        if (local && local.syncStatus === "Pending sync" && local.clientUpdatedAt > event.clientUpdatedAt) {
          continue;
        }
        await db.events.put({ ...event, syncStatus: "Synced" });
      }
      for (const photo of result.photos) {
        const local = await db.photos.get(photo.id);
        await db.photos.put({
          ...photo,
          blob: local?.blob,
          syncStatus:
            photo.verificationStatus === "verified" ? "Synced" : local?.syncStatus === "Pending sync"
              ? "Pending sync"
              : "Synced",
        });
      }
    },
  );
}

function stripLocal<T extends { syncStatus: SyncStatus }>(row: T): Omit<T, "syncStatus"> {
  const { syncStatus: _s, ...rest } = row;
  return rest;
}

function stripPhotoMeta(photo: LocalPhoto) {
  const { syncStatus: _s, blob: _b, ...rest } = photo;
  return rest;
}

export function markPending(): void {
  setStatus("Pending sync");
}
